<template>
  <div
    class="dash-dropdown"
    :class="{
      'dash-dropdown--selected': showOptions,
      disabled: disabled || isOnlyForMyPrefixes
    }"
    v-click-outside="onClickOutside"
    @click="toggleShowOptions"
  >
    <div class="dash-dropdown__icon">
      <fa icon="caret-up" v-if="showOptions" />
      <fa icon="caret-down" v-else />
    </div>
    <div class="dash-dropdown__label" v-if="!showOptions">
      {{ displayLabel }}
    </div>
    <div class="dash-dropdown__input" v-else>
      <input
        type="text"
        :placeholder="placeholder"
        v-model="inputText"
        ref="filterInput"
      />
    </div>
    <div class="dash-dropdown__option-wrapper" v-if="showOptions" ref="wrapper">
      <div
        v-for="item in filteredOptions"
        :key="item.id"
        class="dash-dropdown__option"
        :class="{
          'dash-dropdown__option--selected': selectedPrivate === item.id
        }"
        @click.stop="onClick(item)"
      >
        <span
          v-if="item.label.length > 16 && filteredOptions.length > 2"
          v-tooltip.top="{
            content: item.label,
            classes: 'dash-dropdown__tooltip'
          }"
          >{{ item.label }}</span
        >
        <span v-else>{{ item.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import * as vClickOutside from 'v-click-outside';
import { mapMutations } from 'vuex';
import { ID_OF_MY_PREFIXES_CONTEXT } from '../../constants/routingStatusConstant';
import { SELECTED_COMBO_BOX_TYPE } from '../../constants/common';

export default {
  directives: {
    clickOutside: vClickOutside.directive
  },
  // emit on-select event
  props: {
    placeholder: String,
    disabled: Boolean,
    selected: {
      type: String
    },
    list: {
      type: Array,
      default: [],
      validator: array => {
        if (array.some(i => !(i.id && i.label))) {
          console.error(
            'id and label are required property to use dropdown component'
          );
          return false;
        }
        return true;
      }
    }
  },
  watch: {
    list: {
      immediate: true,
      handler(array) {
        this.options = array;
      }
    },
    selected: {
      immediate: true,
      handler(value) {
        this.selectedPrivate = value;
      }
    },
    showOptions(value) {
      if (!value) {
        return;
      }
      this.inputText = '';

      this.$nextTick(() => {
        const selected = document.querySelector(
          '.dash-dropdown__option--selected'
        );
        if (!selected) {
          return;
        }
        this.$refs['wrapper'].scrollTo(0, selected.offsetTop);
        this.$refs['filterInput'].focus();
      });
    }
  },
  data() {
    return {
      options: [],
      showOptions: false,
      inputText: '',
      selectedPrivate: ''
    };
  },
  methods: {
    ...mapMutations(['setSelectedComboBox']),
    onClick(item) {
      // Set global variable of selectedComboBox as NONE
      this.setSelectedComboBox(SELECTED_COMBO_BOX_TYPE.NONE);

      this.$emit('on-select', item.id);
      this.selectedPrivate = item.id;
      this.showOptions = false;
    },
    findOptionById(id) {
      if (!this.options || !this.options.length) {
        return;
      }
      return this.options.find(i => i.id === id);
    },
    onClickOutside() {
      // Set global variable of selectedComboBox as NONE
      this.setSelectedComboBox(SELECTED_COMBO_BOX_TYPE.NONE);

      this.showOptions = false;
    },
    toggleShowOptions() {
      if (this.isOnlyForMyPrefixes) return;

      if (!this.displayLabel) {
        return;
      }

      // The setTimeout is to solve the side effect of the function onClickOutside
      const showOptions = this.showOptions;
      setTimeout(() => {
        // Set global variable to expand the selected context combobox
        if (!showOptions && this.placeholder) {
          if (this.placeholder === 'Type member account') {
            this.setSelectedComboBox(SELECTED_COMBO_BOX_TYPE.MEMBER_ACCOUNT);
          } else {
            this.setSelectedComboBox(SELECTED_COMBO_BOX_TYPE.ASN_CONTEXT);
          }
        } else {
          this.setSelectedComboBox(SELECTED_COMBO_BOX_TYPE.NONE);
        }
      }, 10);

      this.showOptions = !this.showOptions;
    }
  },
  computed: {
    displayLabel() {
      if (!this.selectedPrivate) {
        if (
          this.filteredOptions[0] &&
          this.filteredOptions[0].id === ID_OF_MY_PREFIXES_CONTEXT
        ) {
          return this.filteredOptions[0].label;
        } else {
          return '';
        }
      }
      const option = this.findOptionById(this.selectedPrivate);
      if (option) {
        return option.label;
      }
      return '';
    },
    filteredOptions() {
      return this.options.filter(i =>
        i.label.toLowerCase().includes(this.inputText.toLowerCase())
      );
    },
    isOnlyForMyPrefixes() {
      return (
        this.list.length === 1 && this.list[0].id === ID_OF_MY_PREFIXES_CONTEXT
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.dash-dropdown {
  display: block;
  position: relative;
  border: 1px solid #fff;
  padding: 1px 5px;
  background: #000;
  width: 208px;
  height: 24px;

  &.disabled {
    user-select: none;
    cursor: not-allowed;
    color: $text-grey;

    .dash-dropdown__icon {
      cursor: not-allowed;
    }

    .dash-dropdown__label {
      cursor: not-allowed;
    }
  }

  &__icon {
    display: inline;
    position: absolute;
    right: 6px;
    top: 1px;
    font-size: 14px;
    cursor: pointer;
  }

  // &:hover {
  //   border: 2px solid #666666 !important;
  //   padding: 0px 4px;
  // }

  &--selected {
    border-width: 2px;
    padding: 0px 4px;
    z-index: 9;

    .dash-dropdown__icon {
      top: 0px;
      right: 5px;
    }
  }

  &__label {
    width: calc(100% - 20px);
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
    cursor: pointer;
  }

  &__input {
    display: flex;
    width: calc(100% - 20px);
    height: 100%;

    input {
      height: 100%;
      width: 100%;
      outline: none;
      border: none;
      background-color: black;
      color: white;
      font-size: 16px;
    }
  }

  &__option-wrapper {
    position: absolute;
    top: 100%;
    left: -2px;
    width: calc(100% + 4px);
    max-height: 500px;
    overflow-y: auto;
    font-size: 16px;
    background-color: white;
    border: 2px solid white;
    color: black;
    box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.4);

    @include for-tablet-portrait-down {
      font-size: 14px;
    }
  }

  &__option {
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0 5px;
    line-height: 32px;

    &:hover {
      background-color: #ccc;
    }

    &:active {
      background-color: $apnic-blue;
      color: white;
    }
  }
  .dash-dropdown__option--selected {
    background-color: #666;
    color: white;
  }
}
</style>
